// @ts-nocheck
/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
/* Starter Custom Code
import {
    HasProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
*/
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
// import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels"; // Foundation
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
// import Button, { ButtonPresentationProps } from "@insite/mobius/Button"; // Starter Custom Code
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

/* Starter Custom Code - Start */
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import PlusCircleFill from "@store/Library/Components/Toolkit/Icons/PlusCircle/PlusCircleFill";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
// import { useCanAddToCart, useHasEnoughInventory } from "@toolkit/Framework/Store/Data/Products/ProductsHooks"; // Foundation
import { css } from "styled-components";

// Foundation -->
import { useAllowBackOrder } from "@store/Framework/Store/Bunzl/Components/ProductAddToCartButton/ProductAddToCartButtonHooks";
import {
    useIsHideSignInToSeePriceButton,
    useIsSignedInAndhasToBeSignedIn,
} from "@store/Framework/Store/Context/ContextHooks";
import useCanAddToCart from "@store/Framework/Store/Data/Products/ProductsHooks/useCanAddToCart";
import SignInToSeePriceButton from "@store/Library/Components/Foundation/SignInToSeePriceButton";
import StoreVariantsButton from "@store/Library/Components/Foundation/StoreVariantsButton";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import { useHasEnoughInventory } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";
import { useCanAddToCart as toolkitUseCanAddToCart } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";
// <--- Foundation

export interface ProductAddToCartButtonProps
    extends Omit<OwnProps, keyof ButtonPresentationProps | keyof LinkPresentationProps> {}

export const useShowProductAddToCartButton = (
    configurationCompleted: boolean | undefined,
    variantSelectionCompleted: boolean | undefined,
) => {
    const productContext = useContext(ProductContext);
    const { product, productInfo } = productContext;
    const hasEnoughInventory = useHasEnoughInventory(productContext);
    const canAddToCart = toolkitUseCanAddToCart(
        product,
        productInfo,
        configurationCompleted,
        variantSelectionCompleted,
    );

    return !((!hasEnoughInventory && !productInfo.failedToLoadInventory) || !canAddToCart);
};
/* Starter Custom Code - End */

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToCartButtonStyles; // Starter Custom Code
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // Starter Custom Code
    overridedStyles?: ProductAddToCartButtonStyles;
    isLink?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings, // Foundation
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog: productSettings.showAddToCartConfirmationDialog, // Foundation
        addToCartPopupTimeout,
        isUpdatingCart, // Starter Custom Code
        // Foundation
        productSettings,
        currentCurency: state.context.session.currency?.currencyCode,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

/* Starter Custom Code - Start */
export interface ProductAddToCartButtonStyles {
    button?: ButtonPresentationProps;
    link?: LinkPresentationProps;
}

export const productAddToCartButtonStyles: ProductAddToCartButtonStyles = {
    button: {
        variant: "primary",
        css: css`
            ${x => x.theme.button.primary.css}
        `,
    },
    link: linkGetActionLinkProps(PlusCircleFill),
};
/* Starter Custom Code - End */

const ProductAddToCartButton = ({
    productContext: {
        product, // Foundation
        productInfo, // Foundation
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    // canAddToCart, // Foundation
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    overridedStyles, // Starter Custom Code
    isLink, // Starter Custom Code
    isUpdatingCart, // Starter Custom Code
    // Foundation --->
    productContext,
    currentCurency,
    productSettings,
    // <--- Foundation
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const trackAddToCart = ga.use.trackAddToCart(); // Starter Custom Code
    const styles = useExpandProps(productAddToCartButtonStyles, extendedStyles, overridedStyles); // Starter Custom Code
    const [isLoading, setIsLoading] = useState(false); // Starter Custom Code

    // Foundation --->
    const isSignedInAndhasToBeSignedIn = useIsSignedInAndhasToBeSignedIn();
    const allowBackOrder = useAllowBackOrder();
    const canAddToCart = useCanAddToCart(
        product,
        productSettings,
        productInfo,
        hasEnoughInventory,
        configurationCompleted,
        variantSelectionCompleted,
        allowBackOrder,
    );
    const isHideSignInToSeePrice = useIsHideSignInToSeePriceButton();

    if (isHideSignInToSeePrice && !isSignedInAndhasToBeSignedIn) {
        return null;
    }

    if (!isSignedInAndhasToBeSignedIn) {
        return <SignInToSeePriceButton {...styles.button} />;
    }

    if (product.isVariantParent) {
        return <StoreVariantsButton path={productInfo.productDetailPath} />;
    }
    // <-- Foundation

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        if (!canAddToCart /* Foundation */) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        setIsLoading(true); // Starter Custom Code

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            // Foundation --->
            productId: product.id,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
            // <--- Foundation
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        setIsLoading(false); // Starter Custom Code
        trackAddToCart(); // Starter Custom Code

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
        }
    };

    return (
        <>
            {/* Starter Custom Code - Start */}
            {isLink ? (
                <Link
                    {...styles.link}
                    onClick={addToCartClickHandler}
                    disabled={productInfo.qtyOrdered <= 0 || isUpdatingCart} // Foundation
                    {...otherProps}
                >
                    {labelOverride ?? translate("Add to Cart")}
                </Link>
            ) : (
                <Button
                    {...styles.button}
                    onClick={addToCartClickHandler}
                    disabled={productInfo.qtyOrdered <= 0 || isAddToCartButtonDisabled || !canAddToCart} // Foundation
                    isLoading={isLoading} // Starter Custom Code
                    {...otherProps}
                >
                    {labelOverride ?? translate("Add to Cart")}
                </Button>
            )}
            {/* Starter Custom Code - End */}
        </>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
